import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Key, Copy, ArrowLeft } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminAccessCode({ onBack }) {
  const [code, setCode] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    axios.get(`${API}/admin/access-code/current`)
      .then((response) => {
        if (!active) return;
        setCode(response.data.code);
        setExpiresAt(response.data.expires_at);
      })
      .catch((err) => {
        if (active && err.response?.status !== 404) setError('Could not load current access code');
      });
    return () => { active = false; };
  }, []);

  const generateCode = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/admin/access-code/generate`);
      setCode(response.data.code);
      setExpiresAt(response.data.expires_at);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not generate access code');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    if (!code) return;
    await navigator.clipboard?.writeText(code);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-xl">
      <button onClick={onBack} className="mb-5 text-sm font-semibold text-green-600 hover:text-green-700 flex items-center gap-1">
        <ArrowLeft size={16} weight="bold" /> Back to Settings
      </button>
      <div className="flex items-center gap-3 mb-2">
        <Key size={28} weight="bold" className="text-purple-600" />
        <h2 className="text-xl font-bold text-gray-800">Admin Access Code</h2>
      </div>
      <p className="text-sm text-gray-500 mb-6">Generate a code for a new admin device. The code expires in 12 hours and replaces the previous code.</p>
      {code && (
        <div className="rounded-lg bg-purple-50 border border-purple-200 p-4 mb-4">
          <p className="text-xs text-purple-700 mb-1">Current access code</p>
          <div className="flex items-center justify-between gap-3">
            <code className="text-2xl tracking-[0.3em] font-bold text-purple-900">{code}</code>
            <button type="button" onClick={copyCode} className="p-2 rounded hover:bg-purple-100" title="Copy access code">
              <Copy size={20} className="text-purple-700" />
            </button>
          </div>
          <p className="text-xs text-purple-700 mt-2">Expires: {new Date(expiresAt).toLocaleString()}</p>
        </div>
      )}
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      <button
        type="button"
        onClick={generateCode}
        disabled={loading}
        className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
      >
        {loading ? 'Generating...' : code ? 'Generate new access code' : 'Generate access code'}
      </button>
    </div>
  );
}
